<template>
  <div
    class="wt-story-lightbox wt2-sg"
    :class="[isPristine ? '-grey' : '-light']"
  >
    <transition name="wt2-fade">
      <div class="-filter-box-container" v-if="!isPristine">
        <div class="-filter-box">
          <lightbox-filters
            :selectedAssetsFilter="selectedAssetsFilter"
            @update:selectedAssetsFilter="changeFilter"
            @search="search"
          />
        </div>
      </div>
    </transition>

    <wt-files-dropzone ref="drop" :show="isDragging">
      <template slot="indicator">
        <i class="wticon wticon-upload2" />
        <span class="-drop-text">{{
          'generic.DND_INVITATION_M1' | culture
        }}</span>
        <span class="-drop-formats">{{
          'generic.DND_INVITATION_M2' | culture
        }}</span>
      </template>
    </wt-files-dropzone>

    <component
      v-if="!isDragging && curBox"
      :is="curBox"
      @open-media-upload-view="changeMediaFile"
      @open-audio-upload-view="changeAudioFile"
      @edit-audio-asset="editAudioAsset"
    />
    <div
      class="-float-action"
      :class="{ '-show': showActions }"
      @mouseenter="onActionMouseEnter"
      @mouseleave="onActionMouseLeave"
      v-if="!isPristine && !isDragging"
    >
      <wt-svg-animation
        name="light-box-search"
        :scene="animationScene"
        v-tooltip="{
          title: 'Search media',
          placement: 'right',
          delay: 0,
          wrapperClass: '-label',
        }"
        @click.native="$api.gotoAssetSearch"
      />

      <div class="-float-hover" :style="floatHoverStyle">
        <div
          v-for="option in floatingOptionsList"
          :key="option.icon"
          class="-button-round"
          v-tooltip="option.tooltipObj"
          @click="option.onClick"
        >
          <i :class="['wticon', option.icon]" />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import app from 'app';
import api from './api';
import SearchMediaBox from './parts/SearchMediaBox.vue';
import AssetsBox from './parts/AssetsBox.vue';
import LightboxFilters from './parts/LightboxFilters.vue';
import { mapState, mapActions } from 'vuex';

export default {
  name: 'wt-story-lightbox',
  components: {
    SearchMediaBox,
    AssetsBox,
    LightboxFilters,
  },

  provide() {
    return this.$api;
  },

  data() {
    const tooltipCss = { placement: 'right', delay: 0, wrapperClass: '-label' };
    const floatingOptions = [
      {
        enabled: true,
        icon: 'wticon-camera',
        onClick: () => {
          this.$api.gotoFaceOnCamera();
        },
        tooltipObj: {
          title: app.culture.get('uploadView.cta.recordVideo'),
          ...tooltipCss,
        },
      },
      {
        enabled: true,
        icon: 'wticon-mic',
        onClick: () => {
          this.changeAudioFile();
        },
        tooltipObj: {
          title: app.culture.get('uploadView.cta.recordAudio'),
          ...tooltipCss,
        },
      },
      {
        enabled: !app.isEmbedded,
        icon: 'wticon-upload2',
        onClick: () => {
          this.changeMediaFile();
        },
        tooltipObj: {
          title: 'Upload (or drag)',
          ...tooltipCss,
        },
      },
      {
        enabled: true,
        icon: 'wticon-Bucket',
        onClick: () => {
          this.$api.gotoWallpaper();
        },
        tooltipObj: {
          title: this.$options.addTitle,
          ...tooltipCss,
        },
      },
    ];

    return {
      guid: app.guid('timeline-lightbox'),
      animationScene: 'mounted',
      showActions: false,
      floatingOptionsList: _.filter(floatingOptions, { enabled: true }),
      floatingOptionHeight: 50,
      curBox: null,
      isPristine: null,
      isDragging: false,
    };
  },

  beforeCreate() {
    this.$api = api(this.$options.extend);
    Object.assign(this.$options, {
      addTitle: app.culture.get('wallpaper.addTitle'),
    });
  },

  beforeDestroy() {
    app.namespace.Util.FileDndDispatcher.remove(this.guid);
    this.$options.offStoreListener();
  },

  created() {
    this.updatePristine();
    this.updateCurBox();
    app.namespace.Util.FileDndDispatcher.add({
      guid: this.guid,
      handlers: {
        dragover: this.$_dragOver,
        dragenter: this.$_dragEnter,
        dragleave: this.$_dragLeave,
        drop: this.$_drop,
        paste: this.$_onPaste,
      },
    });

    this.$options.offStoreListener = this.$store.subscribe(
      (mutation /* , state*/) => {
        const mutations =
          /addLightboxUploadingAsset|addSelectedAsset|removeSelectedAsset|addSelectedVoiceOverSegment|addLightboxUploadingVoiceOverAsset/;
        if (mutations.test(mutation.type)) {
          this.onLightboxChanged();
        }
      }
    );

    this.onActionMouseLeave = _.debounce(
      function () {
        this.animationScene = 'mouseleave';
        this.showActions = false;
      }.bind(this),
      app.TIME.second(0.3),
      { leading: false, trailing: true }
    );
  },

  computed: {
    floatHoverStyle() {
      return {
        height:
          23 +
          (!this.showActions
            ? 0
            : this.floatingOptionsList.length * this.floatingOptionHeight) +
          'px',
      };
    },
    ...mapState({
      storyId: (state) => state.story.id,
      selectedAssetsFilter: (state) => state.story.selectedAssetsFilter,
    }),
  },

  methods: {
    ...mapActions(['FILTER_SELECTED_ASSETS', 'SEARCH_SELECTED_ASSETS']),

    changeFilter(filterFormat) {
      this.FILTER_SELECTED_ASSETS({ filterFormat });
    },

    search(searchQuery) {
      this.SEARCH_SELECTED_ASSETS({ searchQuery });
    },

    onActionMouseEnter() {
      this.animationScene = 'mouseenter';
      this.showActions = true;
      this.onActionMouseLeave.cancel();
    },

    updateCurBox() {
      this.curBox = this.isPristine ? 'search-media-box' : 'assets-box';
    },

    updatePristine() {
      this.isPristine = this.$api.isPristine();
    },

    onLightboxChanged() {
      this.updatePristine();
      this.updateCurBox();
    },

    onFileDropped(files, e) {
      if (!files.length) {
        return;
      }

      this.$api.showMediaUploadView({
        initEvent: e,
        storyId: this.storyId,
        analyticsData: {
          using: 'drag',
          'from screen': 'lightbox',
          source: 'drag',
        },
      });
    },

    $_onPaste(e) {
      const files = app.getFilesFromEvent(e);
      if (!_.isEmpty(files)) {
        this.$api.showMediaUploadView({
          initEvent: e,
          storyId: this.storyId,
          analyticsData: {
            using: 'copy/paste',
            'from screen': 'lightbox',
            source: 'copy & past',
          },
        });
      }
    },

    $_dragOver(e) {
      const dt = e.dataTransfer;
      if (dt.types && dt.types.indexOf('Files') >= 0) {
        e.preventDefault();
        e.stopPropagation();
        dt.dropEffect = 'copy';
      } else {
        this.isDragging = false;
      }
    },

    $_dragEnter: function (e) {
      e.preventDefault();
      if (e.fromElement === null && !app.isEmbedded) {
        this.isDragging = true;
      }
    },

    $_dragLeave: function (e) {
      e.preventDefault();
      if (e.fromElement === null) {
        this.isDragging = false;
      }
    },

    $_drop: function (e) {
      e.preventDefault();
      if (this.$refs.drop.active) {
        if (this.$refs.drop.onDrop(e)) {
          const files = e.dataTransfer.files;
          this.onFileDropped(files, e);
        }
      }
      this.isDragging = false;
    },

    changeMediaFile: function (e) {
      this.$api.showMediaUploadView({
        initEvent: e,
        storyId: this.storyId,
        analyticsData: {
          using: 'button',
          'from screen': 'lightbox',
          source: this.isPristine
            ? 'menu on empty lightbox'
            : 'menu on full lightbox',
        },
      });
    },

    editAudioAsset: function (asset) {
      const voiceId = asset.metadata.voiceId;
      const transcript = asset.metadata.transcript;
      this.$api.showAudioRecordView({
        asset,
        voiceId,
        transcript,
        initEvent: null,
        storyId: this.storyId,
        analyticsData: {
          using: 'button',
          'from screen': 'lightbox',
          source: this.isPristine
            ? 'menu on empty lightbox'
            : 'menu on full lightbox',
        },
        aiVoiceSpeed: asset.metadata.speechSpeed || 1.0,
      });
    },

    changeAudioFile: function (e) {
      this.$api.showAudioRecordView({
        initEvent: e,
        storyId: this.storyId,
        analyticsData: {
          using: 'button',
          'from screen': 'lightbox',
          source: this.isPristine
            ? 'menu on empty lightbox'
            : 'menu on full lightbox',
        },
      });
    },
  },
};
</script>

<style lang="scss">
.wt-story-lightbox {
  width: 100%;
  height: 100%;
  display: flex;
  flex-flow: column;
  // position: relative;
  // overflow: hidden;

  .-filter-box-container {
    position: relative;
    width: 100%;
  }
  .-filter-box {
    z-index: 10;
    position: absolute;
    display: flex;
    right: 30px;
    top: 5px;
    justify-content: space-between;
    align-items: center;
    justify-content: flex-end;
    width: 250px;
  }

  .wt-files-dropzone {
    width: 100%;
    height: 100%;
    background-color: rgba($color-white, 0.85);
    z-index: 2;

    .-drop-text {
      font-size: 25px;
      font-weight: 400;
      margin-top: 9px;
    }

    .-drop-formats {
      font-size: 15px;
      font-weight: 400;
      margin-top: 5px;
    }

    i {
      font-size: 41px;
    }
  }

  &.-light {
    background-image: url('https://dp4stoslb9jdl.cloudfront.net/Resources/studio/img/pattern-white.png');
  }

  &.-grey {
    background-image: url('https://dp4stoslb9jdl.cloudfront.net/Resources/studio/img/pattern-grey.png');
  }

  .-button-round {
    display: flex;
    justify-content: center;
    align-items: center;
    width: 45px;
    height: 45px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
    border: 1px solid rgba(0, 0, 0, 0.05);
    background-color: $color-white;
    color: $color-normal;
    border-radius: 50%;
    cursor: pointer;
    transition: all 0.2s;

    &.-bigger {
      width: 55px;
      height: 55px;
      margin-left: -5px;
      margin-bottom: -5px;
    }

    &.-fixed-colors {
      background-color: $search-button-bg;
      color: $color-white;
    }

    &:hover {
      box-shadow: 0 0 12px rgba($color-black, 0.4);
    }

    i {
      font-size: 19px;
      &.wticon-camera {
        font-size: 13px;
      }
    }
  }

  .-float-action {
    position: relative;
    height: 0;
    display: flex;
    align-items: flex-end;
    @include user-select(none);
    transform: translate(20px, -20px);
    z-index: 10;

    .-bigger {
      z-index: 1;

      .-opened {
        opacity: 0;
        font-size: 0;
      }

      .-closed {
        transition: opacity 0.2s;
      }

      .-opened {
        transition: opacity 0.2s 0.05s;
      }
    }

    .wt-svg-animation {
      width: 55px;
      height: 55px;
      transform: translate(-10px, 10px);
      cursor: pointer;

      &:after {
        content: '';
        position: absolute;
        top: 1px;
        left: 1px;
        width: 97%;
        height: 97%;
        border-radius: 50%;
      }

      &:hover {
        &:after {
          box-shadow: 0 0 12px rgba($color-black, 0.4);
        }
      }
    }

    .-float-hover {
      position: absolute;
      margin-bottom: 5px;
      opacity: 0;
      transition: opacity 0.2s, height 0.2s;
      transform: translate(-10px, -44px);
      height: 0;
      width: 55px;
      padding: 7px 5px 0 5px;

      .-button-round {
        transition: margin-bottom 0.3s;
      }
    }

    &:hover {
      .-bigger {
        .-closed {
          opacity: 0;
          font-size: 0;
        }

        .-opened {
          opacity: 1;
          font-size: 19px;
        }
      }
    }

    &.-show .-float-hover {
      opacity: 1;

      .-button-round {
        margin-bottom: 10px;
      }
    }
  }
}
</style>
